import { CurrencyCode } from 'common/types/format';
import { VALUATIONS_SPREADSHEET_ENTERPRISE_VALUE_KEY } from 'pages/ValuationsAllocation/common/constants/valuations';

type CurrencyType = {
  currencyCode: CurrencyCode;
  currencySymbol: string;
};

type CompanyType = {
  company_name: string;
  measurement_date?: string;
};

export const FORWARD_MULTIPLE_DISCOUNT = 'Forward Multiple Discount';
export const FORWARD_MULTIPLE_DISCOUNT_ALIAS = 'forward_multiple_discount';
export const MEDIAN_ALIAS = 'median';
export const MEAN_ALIAS = 'mean';
export const MAX_ALIAS = 'max';
export const MIN_ALIAS = 'min';
export const SEVENTY_FIFTH_ALIAS = '75th';
export const TWENTY_FIFTH_ALIAS = '25th';
export const PERCENTILE_SELECTION_A_ALIAS = 'percentile_selection_a';
export const PERCENTILE_SELECTION_B_ALIAS = 'percentile_selection_b';
export const PERCENTILE_ROWS = [PERCENTILE_SELECTION_A_ALIAS, PERCENTILE_SELECTION_B_ALIAS];
export const PERCENTILE_75 = '75th Percentile';
export const PERCENTILE_25 = '25th Percentile';
export const EBITDA_SELECTION_ALIAS = 'ebitda_selection';
export const EBITDA_OPTION = 'EBITDA';
export const EBITDA_OPTION_VALUE = 0;
export const ADJUSTED_EBITDA_OPTION = 'Adjusted EBITDA';
export const ADJUSTED_EBITDA_OPTION_VALUE = 1;
export const EBITDA_SELECTION_OPTIONS = (isHistoricalPeriod: boolean) => [
  {
    value: EBITDA_OPTION_VALUE,
    label: EBITDA_OPTION,
    tooltip: isHistoricalPeriod ? 'GAAP EBITDA' : 'Capital IQ Consensus Estimates',
  },
  ...(isHistoricalPeriod
    ? [
      {
        value: ADJUSTED_EBITDA_OPTION_VALUE,
        label: ADJUSTED_EBITDA_OPTION,
        tooltip: 'EBITDA adding back Stock-Based Compensation',
      },
    ]
    : []),
];

export const WEIGHTED_LABEL = 'Weighted';
export const SPECIFIED_LABEL = 'Specified';
export const MEAN = 'Mean';
export const MEDIAN = 'Median';
export const MAX = 'Max';
export const MIN = 'Min';

// WARNING: DON'T rearrange these, as their order effects a cell in the GPC
// Note: Valid value for database is 'Selected' (not 'Specified'). Handled in reverseParser.
// ^ thats scary....
export const VALUES_OPTIONS = [MEDIAN, MEAN, PERCENTILE_75, PERCENTILE_25, SPECIFIED_LABEL];
export const VALUES_OPTIONS_WITHOUT_SPECIFIED = [MEDIAN, MEAN, PERCENTILE_75, PERCENTILE_25, MAX, MIN];
export const LIMITED_VALUES_OPTIONS = [MEDIAN, MEAN, SPECIFIED_LABEL];
export const SINGLE_COMPARISON_OPTIONS = [SPECIFIED_LABEL];

export const ADD_COMP = 'Add Comparable Company';
export const ADD_PUBLIC = 'Add Public Company';
export const SELECTED_DATABASE_VALUE = 'Selected';
export const PLUS_CASH_ALIAS = 'plus_cash';
export const LESS_DEBT_ALIAS = 'less_debt';
export const GPC_SUMMARY = 'gpc_summary';
export const COMPANY_TICKER = 'company-ticker';
export const COMPANY = 'company';
export const COMPANIES = 'companies';
export const SELECTION = 'selection';
export const SELECTED_MULTIPLE_VALUE = 'selected_multiple_value';
export const SELECTED_MULTIPLE = 'selected_multiple';
export const WEIGHTING = 'weighting';
export const WEIGHTED_EV = VALUATIONS_SPREADSHEET_ENTERPRISE_VALUE_KEY;
export const WEIGHTED_EQUITY_VALUE = 'equity_value';

export const STOCK_PRICE_ID = 'close_price';
export const MARKET_CAP_ID = 'market_cap';
export const TICKER_SYMBOL_ID = 'ticker_symbol';

export const FIRST_NON_FORWARD_ID = 'first_non_forward';
export const FIRST_FORWARD_ID = 'first_forward';
export const SECOND_NON_FORWARD_ID = 'second_non_forward';
export const SECOND_FORWARD_ID = 'second_forward';

export const MAIN_COLUMNS_IDS = [FIRST_NON_FORWARD_ID, FIRST_FORWARD_ID, SECOND_NON_FORWARD_ID, SECOND_FORWARD_ID];

export const BENCHMARK_ALIASES = [
  MEDIAN_ALIAS,
  MEAN_ALIAS,
  SEVENTY_FIFTH_ALIAS,
  TWENTY_FIFTH_ALIAS,
  PERCENTILE_SELECTION_A_ALIAS,
  PERCENTILE_SELECTION_B_ALIAS,
  SELECTED_MULTIPLE,
];

export const ENTERPRISE_VALUE_ID = VALUATIONS_SPREADSHEET_ENTERPRISE_VALUE_KEY;
export const MARKET_CAP_COLUMN_LEGEND = 'C';
export const ENTERPRISE_VALUE_COLUMN_LEGEND = 'D';

export const LTM_REVENUE = 'ltm_revenue';
export const LTM_EBITDA = 'ltm_ebitda';
export const NTM_REVENUE = 'ntm_revenue';
export const NTM_EBITDA = 'ntm_ebitda';
export const FORWARD_NET_INCOME = 'forward_net_income';
export const NET_INCOME = 'net_income';
export const NET_INCOME_EXCLUDE_EXTRA_ITEMS = 'net_income_to_common_excl_extra_items';
export const TANGIBLE_BOOK_VALUE = 'tangible_book_value';
export const TANGIBLE_BOOK_VALUE_ESTIMATE = 'tangible_book_value_estimate';

export const LTM_NTM_COLUMNS_ALIAS = [LTM_REVENUE, LTM_EBITDA, NTM_REVENUE, NTM_EBITDA];

export const FIRST_NON_FORWARD_COLUMN_LEGEND = 'E';
export const SECOND_NON_FORWARD_COLUMN_LEGEND = 'F';
export const FIRST_FORWARD_COLUMN_LEGEND = 'G';
export const SECOND_FORWARD_COLUMN_LEGEND = 'H';
export const middleColumnLetters = ['A', 'B', 'C', 'D'];
export const mainColumnLetters = [
  FIRST_NON_FORWARD_COLUMN_LEGEND,
  SECOND_NON_FORWARD_COLUMN_LEGEND,
  FIRST_FORWARD_COLUMN_LEGEND,
  SECOND_FORWARD_COLUMN_LEGEND,
];
export const ltmColumnLetters = mainColumnLetters.slice(0, 2);
export const ntmColumnLetters = mainColumnLetters.slice(2, 4);

export const PRICE_TO_TANGIBLE_PERIOD = 'price_to_tangible_period';
export const PRICE_TO_EARNINGS_PERIOD = 'price_to_earnings_period';
export const PRICE_TO_EARNINGS_ABV = 'price';
export const TANGIBLE_BOOK_VALUE_ABV = 'tangible';
export const FORWARD_PRICE_TO_TANGIBLE_PERIOD = 'forward_price_to_tangible_period';
export const FORWARD_PRICE_TO_EARNINGS_PERIOD = 'forward_price_to_earnings_period';

export const STOCK_PRICE = 'Stock Price as of Measurement Date';
export const MARKET_CAP_MILLIONS = ({ currencyCode, currencySymbol }: CurrencyType) =>
  `Market Cap in ${currencyCode} (${currencySymbol}) Millions`;
export const REVENUE = 'Revenue Multiple';
export const EBITDA = 'EBITDA Multiple';
export const ENTERPRISE_VALUE_MILLIONS = ({ currencyCode, currencySymbol }: CurrencyType) =>
  `Enterprise Value in ${currencyCode} (${currencySymbol}) Millions`;

export const WARNING_GETTING_COMPANIES =
  'Could not get valid information from all the companies in the comp group, only the valid ones will be added.';
export const ERROR_GETTING_COMPANIES = 'Could not get valid information from the companies in the comp group';
export const NO_COMPANIES_FOUND = 'No companies found. Please enter another search term and try again.';
export const ONE_COMPANY_FOUND = '1 company found. Select it to add it.';
export const MULTIPLE_COMPANIES_FOUND = 'companies found. Select a company to add.';
export const TICKERS = 'tickers';
export const INDEX_TICKERS = 'index-tickers';
export const MULTIPLE_TICKERS = 'Enter Multiple Tickers';
export const COMPANY_OR_TICKER = 'Company or Ticker';
export const RETRIEVING_COMPANY = 'Retrieving company data...';
export const SEARCHING = 'Searching...';
export const SEARCH_DELAY = 300;
export const TICKER_SEPARATORS = [';', ', ', ' '];
export const SYMBOL_NOT_FOUND = 'symbol not found';
export const SINGLE_COMPANY_RESULTS = 1;
export const ENTER_SEARCH_TEXT = 'Type in the box to search for companies';
export const SEARCH_BY = 'Search By:';
export const ENTERPRISE_VALUE_OPTION = 'Enterprise Value';
export const EV_EXCL_OPERATING_LEASES_OPTION = 'EV Excl Op Leases';
export const EV_EXCL_OP_LEASES_ALIAS = 'ev_excl_operating_leases';
export const MULTIPLE_BASIS_OPTIONS = [ENTERPRISE_VALUE_OPTION, EV_EXCL_OPERATING_LEASES_OPTION];
export const ENTERPRISE_VALUE_COLUMN_ID = VALUATIONS_SPREADSHEET_ENTERPRISE_VALUE_KEY;
export const MULTIPLE_BASIS_ALIAS = 'multiple_basis';
export const PERCENTILE_RANKING_TITLE = 'Percentile Ranking';

export const COMPANY_NOT_PUBLIC = ({ company_name, measurement_date }: CompanyType) =>
  `There isn't available data for ${company_name} because it isn't public or is no longer public as of
  the ${measurement_date ? 'selected measurement date' : 'current date'}. This company will not be added.`;

export const COMPANY_PRESENT_IN_TABLE = ({ company_name }: CompanyType) =>
  `The company ${company_name} is already present in the table.`;

export const SOME_COMPANIES_NOT_PUBLIC = (measurementDate: string) =>
  `There are no available data for some companies as they were not public or no longer public at the ${
    measurementDate ? 'selected measurement date' : 'current date'
  }. These companies will not added.`;

export const PUBLIC_COMPS_APPROACH_ALIAS = 'public_comps_approach';

export const VALUATION_GPC_TABLE_SLUG = 'valuation-gpc';
export const VALUATION_GPC_LTM_DROPDOWN_KEY = 'E1';
export const VALUATION_GPC_EXTRA_INFO_TOOLTIP_TITLE = 'Click here to hide the extra info';
export const VALUATION_EXPAND_GPC_EXTRA_INFO_KEY = 'expandGPCExtraInfo';
export const NTM_REVENUE_MULTIPLE = 'ntm_revenue_multiple';
export const NTM_EBITDA_MULTIPLE = 'ntm_ebitda_multiple';
export const MULTIPLE_DISCOUNT_COLUMN_LEGEND = [NTM_REVENUE_MULTIPLE, NTM_EBITDA_MULTIPLE];

export const LTM_EBITDA_CUSTOM_KEY = 'ltm_ebitda_selected_multiple_value';
export const NTM_EBITDA_CUSTOM_KEY = 'ntm_ebitda_selected_multiple_value';
export const LTM_REVENUE_CUSTOM_KEY = 'ltm_revenue_selected_multiple_value';
export const NTM_REVENUE_CUSTOM_KEY = 'ntm_revenue_selected_multiple_value';

export const MAP_EBITDA_SUFFIX_VALUE = 'ebitda';
// Regular constant for the fields in the database
export const MAP_ADJUSTED_EBITDA_SUFFIX_VALUE = 'adjusted_ebitda';

export const EBITDA_SELECTION_ROW_TRANSFORMER_MAP: { [key: string]: string } = {
  [EBITDA_OPTION]: MAP_EBITDA_SUFFIX_VALUE,
  [ADJUSTED_EBITDA_OPTION]: MAP_ADJUSTED_EBITDA_SUFFIX_VALUE,
};
// Special constant for custom serializer fields
export const ADJUSTED_EBITDA_FOR_YEARS = 'adjebitda';

// Configuration cells
export const FIRST_PERIOD_SELECTION_CELL = 'E1';
export const SECOND_PERIOD_SELECTION_CELL = 'G1';
export const FIRST_PERIOD_EBITDA_SELECTION_CELL = 'F2';
export const SECOND_PERIOD_EBITDA_SELECTION_CELL = 'H2';

// EBITDA Selection Columns
export const FIRST_PERIOD_EBITDA_SELECTION_COLUMN = 'F';
export const SECOND_PERIOD_EBITDA_SELECTION_COLUMN = 'H';

// Offset due to title rows
export const TITLE_ROWS_OFFSET = 2;
